
import React, { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from "@/hooks/use-toast";

const TAB_DATA = [
  { key: "tenant", label: "باحث" },
  { key: "landlord", label: "مالك" },
  { key: "service_provider", label: "صاحب خدمة" }
] as const;

const Register = () => {
  const [tab, setTab] = useState<"tenant" | "landlord" | "service_provider">("tenant");
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: ''
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    
    if (formData.password !== formData.confirmPassword) {
      setMsg("كلمة المرور غير متطابقة");
      toast({
        title: "خطأ",
        description: "كلمة المرور غير متطابقة",
        variant: "destructive"
      });
      return;
    }

    if (formData.password.length < 6) {
      setMsg("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      toast({
        title: "خطأ",
        description: "كلمة المرور يجب أن تكون 6 أحرف على الأقل",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // منع المالك المسجل من إنشاء حساب كباحث مرة أخرى
      if (tab === 'tenant') {
        const { data: existingData } = await supabase.auth.getUser();
        
        if (existingData.user) {
          const { data: existingProfile } = await supabase
            .from('landlords')
            .select('*')
            .eq('user_id', existingData.user.id)
            .maybeSingle();
            
          if (existingProfile) {
            const errorMessage = 'هذا الحساب مسجل بالفعل كمالك. لا يمكن تسجيله كباحث';
            setMsg(errorMessage);
            toast({
              title: "خطأ",
              description: errorMessage,
              variant: "destructive"
            });
            setLoading(false);
            return;
          }
        }
      }

      // إنشاء حساب باحثين مباشرة باستخدام Supabase Auth (مع إرسال رسالة التفعيل تلقائياً)
      if (tab === 'tenant') {
        const userMetadata: Record<string, string> = {
          full_name: formData.name,
          phone: formData.phone,
          user_type: tab,
        };

        const { data, error } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: userMetadata,
            emailRedirectTo: window.location.origin + '/login',
          }
        });

        if (error) {
          console.error('Registration error:', error);
          
          let errorMessage = error.message;
          if (error.message.includes('User already registered')) {
            errorMessage = 'هذا البريد الإلكتروني مسجل بالفعل';
          } else if (error.message.includes('موجود بالفعل')) {
            errorMessage = error.message;
          }
          
          setMsg(errorMessage);
          toast({
            title: "خطأ في التسجيل",
            description: errorMessage,
            variant: "destructive"
          });
          setLoading(false);
          return;
        }

        if (data.user) {
          const successMessage = "تم إنشاء الحساب بنجاح! تحقق من بريدك لتفعيل الحساب.";
          const toastDescription = "تم إرسال رسالة تفعيل الحساب إلى بريدك الإلكتروني.";

          setMsg(successMessage);
          toast({
            title: "تم إنشاء الحساب",
            description: toastDescription,
            variant: "default"
          });

          setTimeout(() => {
            navigate("/login");
          }, 2000);
        }

        setLoading(false);
        return;
      }

      // إنشاء حساب مالك عن طريق دالة Edge Function الخاصة
      if (tab === 'landlord') {
        const { data, error } = await supabase.functions.invoke('register-landlord', {
          body: {
            email: formData.email,
            password: formData.password,
            full_name: formData.name,
            phone: formData.phone,
          },
        });

        if (error || (data && (data as any).error)) {
          const errorMessage = (data as any)?.error || error?.message || 'حدث خطأ أثناء إنشاء الحساب';
          console.error('Landlord registration error:', errorMessage);
          setMsg(errorMessage);
          toast({
            title: "خطأ في التسجيل",
            description: errorMessage,
            variant: "destructive"
          });
          setLoading(false);
          return;
        }

        const successMessage = (data as any)?.message || "تم إنشاء الحساب بنجاح! تحقق من بريدك لتفعيل الحساب.";
        const toastVariant = (data as any)?.emailSent === false ? "destructive" : "default";

        setMsg(successMessage);
        toast({
          title: (data as any)?.emailSent === false ? "تعذر إرسال رسالة التفعيل" : "تم إنشاء الحساب",
          description: successMessage,
          variant: toastVariant as any
        });

        setTimeout(() => {
          navigate("/login");
        }, 2000);

        setLoading(false);
        return;
      }

      // إنشاء حساب صاحب خدمة عن طريق دالة Edge Function الخاصة
      if (tab === 'service_provider') {
        const { data, error } = await supabase.functions.invoke('register-service-provider', {
          body: {
            email: formData.email,
            password: formData.password,
            full_name: formData.name,
            phone: formData.phone,
            company_name: formData.companyName,
          },
        });

        if (error || (data && (data as any).error)) {
          const errorMessage = (data as any)?.error || error?.message || 'حدث خطأ أثناء إنشاء الحساب';
          console.error('Service provider registration error:', errorMessage);
          setMsg(errorMessage);
          toast({
            title: "خطأ في التسجيل",
            description: errorMessage,
            variant: "destructive"
          });
          setLoading(false);
          return;
        }

        const successMessage = "تم إنشاء الحساب بنجاح! تحقق من بريدك لتفعيل الحساب. بعد التفعيل سيقوم المشرف بتفعيل اشتراكك لإضافة الخدمات.";
        const toastDescription = "تم إرسال رسالة تفعيل الحساب إلى بريدك الإلكتروني، وسيقوم المشرف لاحقاً بتفعيل اشتراكك لإضافة الخدمات.";

        setMsg(successMessage);
        toast({
          title: "تم إنشاء الحساب",
          description: toastDescription,
          variant: "default"
        });

        setTimeout(() => {
          navigate("/login");
        }, 2000);

        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setMsg("حدث خطأ غير متوقع. حاول مرة أخرى.");
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع. حاول مرة أخرى.",
        variant: "destructive"
      });
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-blue-400 to-blue-300 flex items-center justify-center font-['Tajawal',sans-serif]">
      <div className="bg-white shadow-2xl rounded-3xl flex flex-col md:flex-row w-full max-w-4xl overflow-hidden">
        <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col">
          <div className="mb-6">
            <div className="flex bg-gray-100 rounded-full mb-4">
              {TAB_DATA.map(t => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key as "tenant" | "landlord" | "service_provider")}
                  className={`flex-1 py-2 font-bold text-sm md:text-lg transition rounded-full ${
                    tab === t.key
                      ? "bg-blue-500 text-white"
                      : "bg-transparent text-gray-800"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <span className="block text-2xl text-right font-bold text-gray-700">
              {tab === "tenant" ? "إنشاء حساب (باحث)" : tab === "landlord" ? "إنشاء حساب (مالك)" : "إنشاء حساب (صاحب خدمة)"}
            </span>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit} dir="rtl">
            <div>
              <Label>الاسم الكامل</Label>
              <Input 
                type="text" 
                value={formData.name} 
                onChange={e => handleInputChange("name", e.target.value)} 
                placeholder="اسمك الكامل" 
                required 
                className="text-right"
              />
            </div>
            <div>
              <Label>رقم الهاتف</Label>
              <Input 
                type="tel" 
                value={formData.phone} 
                onChange={e => handleInputChange("phone", e.target.value)} 
                placeholder="رقم الهاتف" 
                required 
                className="text-right"
              />
            </div>
            {tab === 'service_provider' && (
              <div>
                <Label>اسم المؤسسة/الشركة</Label>
                <Input 
                  type="text" 
                  value={formData.companyName} 
                  onChange={e => handleInputChange("companyName", e.target.value)} 
                  placeholder="اسم المؤسسة" 
                  required 
                  className="text-right"
                />
              </div>
            )}
            <div>
              <Label>البريد الإلكتروني</Label>
              <Input 
                type="email" 
                value={formData.email} 
                onChange={e => handleInputChange("email", e.target.value)} 
                placeholder="example@email.com" 
                required 
                className="text-right"
              />
            </div>
            <div>
              <Label>كلمة المرور</Label>
              <Input 
                type="password" 
                value={formData.password} 
                onChange={e => handleInputChange("password", e.target.value)} 
                placeholder="كلمة المرور (6 أحرف على الأقل)" 
                required 
                minLength={6}
                className="text-right"
              />
            </div>
            <div>
              <Label>تأكيد كلمة المرور</Label>
              <Input 
                type="password" 
                value={formData.confirmPassword} 
                onChange={e => handleInputChange("confirmPassword", e.target.value)} 
                placeholder="اعد كتابة كلمة المرور" 
                required 
                className="text-right"
              />
            </div>
            
            {msg && (
              <div className={`text-sm text-center p-2 rounded ${
                msg.includes('خطأ') || msg.includes('غير') 
                  ? 'text-red-600 bg-red-50' 
                  : 'text-green-600 bg-green-50'
              }`}>
                {msg}
              </div>
            )}
            
            <Button 
              type="submit"
              className="w-full bg-blue-500 hover:bg-blue-600" 
              disabled={loading}
            >
              {loading ? "جاري التسجيل..." : "إنشاء الحساب"}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <span className="text-gray-500">هل لديك حساب؟ </span>
            <Link to="/login" className="text-blue-600 hover:underline">تسجيل الدخول</Link>
          </div>
        </div>
        
        <div className="hidden md:flex w-1/2 flex-col justify-center items-center bg-gradient-to-br from-blue-500 to-blue-400 text-white p-8">
          <h2 className="text-3xl font-bold mb-3 text-center">مرحباً بك في كنف</h2>
          <p className="text-lg text-center mb-6">سجل الدخول أو أنشئ حسابك جديدًا كمالك أو باحث عن سكن</p>
          <div className="text-sm text-center opacity-90">
            <p>• ابحث عن العقارات المناسبة</p>
            <p>• احفظ العقارات المفضلة</p>
            <p>• تواصل مع الملاك مباشرة</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
